import React, {MouseEvent} from "react";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {selectDisplayName} from "../ClientProfile/approvedProfileSlice";
import {useHistory} from "react-router-dom";
import {AlertBanner, Button, Dropdown, DropdownItem, PresentationPaneHeader} from "../../components";
import {DropdownGroup} from "xps-react";
import {setAssetReliancePageActionMenu} from "./AssetRelianceSlice";
import {AssetRelianceButtonState, FveDiscountRateType} from "./AssetRelianceButtonState";
import {LifeInsuranceSummary} from "../../Assets/models/Assets";
import {setAccordionTableState} from "../../components/AccordionTable/AccordionTableSlice";
import {InvestorGroupType} from "../models/InvestorGroupType";
import {LifeInsurance} from "../../Assets/models/LifeInsurance";
import {useAnalyticsTrackers} from 'src/utils/useAnalyticsTrackers';


interface AssetRelianceHeaderProps {
    id: string;
    title: string;
    investorGroup: InvestorGroupType;
    headerOptions: AssetRelianceButtonState;
    showPageActionMenu: boolean;
    lifeInsurances?: LifeInsuranceSummary;
}

const AssetRelianceHeader: React.FC<AssetRelianceHeaderProps> = ({
                                                                     id,
                                                                     title,
                                                                     investorGroup,
                                                                     headerOptions,
                                                                     showPageActionMenu,
                                                                     lifeInsurances,
                                                                 }) => {
    const displayName = useAppSelector(selectDisplayName) ?? "";
    const dispatch = useAppDispatch();
    const history = useHistory();
    const {track, page} = useAnalyticsTrackers();

    const handleFveDiscountRate = (selectedFveDiscountRate: FveDiscountRateType) => {
        return (e: MouseEvent<HTMLElement>) => {
            dispatch(setAssetReliancePageActionMenu({
                ...headerOptions,
                selectedFveDiscountRate: selectedFveDiscountRate,
            }));
            e.stopPropagation();
            if (FveDiscountRateType.TRIPLE_NET === selectedFveDiscountRate) {
                page("Click_AssetReliance_MenuDots_TRIPLE_NET")
                track("Click_AssetReliance_MenuDots_TRIPLE_NET");
            } else {
                page("Click_AssetReliance_MenuDots_NOMINAL_NET")
                track("Click_AssetReliance_MenuDots_NOMINAL_NET");
            }
        };
    }

    function handleShowExcessAssets() {
        return (e: React.MouseEvent<HTMLInputElement>) => {
            dispatch(setAssetReliancePageActionMenu({
                ...headerOptions,
                showExpectedExcessAsset: !headerOptions.showExpectedExcessAsset
            }));
            dispatch(setAccordionTableState({
                expanded: {[`${id}-review-asset-reliance-excess-assets`]: !headerOptions.showExpectedExcessAsset}
            }));
            e.stopPropagation();
            page("Click_AssetReliance_MenuDots_ShowEstimatedExcess")
            track("Click_AssetReliance_MenuDots_ShowEstimatedExcess");
        };
    }

    function handleIncludeLifeInsuranceAtDeath() {
        return (e: React.MouseEvent<HTMLInputElement>) => {
            dispatch(setAssetReliancePageActionMenu({
                ...headerOptions,
                includeLifeInsuranceAtDeath: !headerOptions.includeLifeInsuranceAtDeath
            }));
            e.stopPropagation();
            page("Click_AssetReliance_MenuDots_LifeInsuranceAtDeath")
            track("Click_AssetReliance_MenuDots_LifeInsuranceAtDeath");
        };
    }

    function showLifeInsuranceCheckbox(lifeinsurance: LifeInsurance[]): boolean {
        const filteredInsurance = lifeinsurance.filter(lifeInsurance => lifeInsurance.isCashValueWillFundGoals)
        return lifeinsurance.length !== filteredInsurance.length;
    }

    const {ageFrom, numberOfYears} = investorGroup.planningPeriod;


    const titleRowChildren = () => {
        return (
            <div className={"asset-reliance-header-container-right"}>
                {showPageActionMenu &&
                    <Dropdown
                        className={"asset-reliance-menu-options"}
                        dropdownKind={"menu"}
                        panelWidth={260}
                        aria-label="show-asset-reliance-options"
                        key={"page-action-menu"}
                        buttonIcon={"only"}
                        iconNameOpen={"menu_dots"}
                        iconNameClose={"menu_dots"}
                        buttonKind={"secondary"}
                        buttonSize={"medium"}
                        inverse={true}
                        rounded={true}
                        alignRight={true}
                    >
                        <DropdownGroup groupName=" " key={"ls-dropdown-group"} show={true}>
                            <DropdownItem className="asset-reliance-page-action-menu-options"
                                          value="show-expected-excess-at-age" itemText="">
                                <div className="show-fve-options"
                                     onClick={handleShowExcessAssets()}>
                                    <input
                                        id="showExpectedExcessAtAge"
                                        name="show-expected-excess-at-age"
                                        type="checkbox"
                                        style={{marginRight: '10px'}}
                                        aria-label="show-expected-excess-at-age"
                                        key={"show-expected-excess-at-age"}
                                        onClick={handleShowExcessAssets()}
                                        checked={headerOptions.showExpectedExcessAsset}
                                    />
                                    Show Estimated Excess at Age {ageFrom + numberOfYears}
                                </div>
                            </DropdownItem>
                            {showLifeInsuranceCheckbox(lifeInsurances!.data) &&
                                <DropdownItem className="asset-reliance-page-action-menu-options"
                                              value="include-life-insurance-at-death" itemText="">
                                    <div className="show-fve-options"
                                         onClick={handleIncludeLifeInsuranceAtDeath()}>
                                        <input
                                            data-testid={"include-life-insurance-at-death"}
                                            id="includeLifeInsuranceAtDeath"
                                            name="include-life-insurance-at-death"
                                            type="checkbox"
                                            style={{marginRight: '10px'}}
                                            aria-label="include-life-insurance-at-death"
                                            key={"include-life-insurance-at-death"}
                                            onClick={handleIncludeLifeInsuranceAtDeath()}
                                            checked={headerOptions.includeLifeInsuranceAtDeath}
                                        />
                                        Include Life Insurance at Death
                                    </div>
                                </DropdownItem>}
                        </DropdownGroup>
                        <DropdownGroup groupName="Growth Rate" key={"ls-dropdown-group"}>
                            <DropdownItem key="tndr">
                                <div className="asset-reliance-page-action-menu-options"
                                     onClick={handleFveDiscountRate(FveDiscountRateType.TRIPLE_NET)}>
                                    <input name="triple-net-discount"
                                           data-testid={"triple-net-discount-option"}
                                           type="radio" value='tndr'
                                           style={{marginRight: '10px'}}
                                           aria-label="triple-net-discount-option"
                                           checked={headerOptions.selectedFveDiscountRate === FveDiscountRateType.TRIPLE_NET}
                                           onClick={handleFveDiscountRate(FveDiscountRateType.TRIPLE_NET)}
                                           disabled={!headerOptions.showExpectedExcessAsset}
                                    />
                                    Triple Net Growth Rate
                                </div>
                            </DropdownItem>
                            <DropdownItem key={"dr-dropdown-group"}>
                                <div className="asset-reliance-page-action-menu-options"
                                     onClick={handleFveDiscountRate(FveDiscountRateType.NOMINAL_NET)}>
                                    <input name="nominal-net-discount"
                                           data-testid={"nominal-net-discount-option"}
                                           type="radio" value='nndr'
                                           style={{marginRight: '10px'}}
                                           aria-label="nominal-net-discount-option"
                                           checked={headerOptions.selectedFveDiscountRate === FveDiscountRateType.NOMINAL_NET}
                                           onClick={handleFveDiscountRate(FveDiscountRateType.NOMINAL_NET)}
                                           disabled={!headerOptions.showExpectedExcessAsset}
                                    />
                                    Nominal Net Growth Rate
                                </div>
                            </DropdownItem>
                        </DropdownGroup>
                    </Dropdown>}
                <span className="paddingleft-12"></span>
                <Button rounded={true}
                        size={"medium"}
                        kind={"borderless"}
                        icon={"left"}
                        iconName={"edit"}
                        className={"edit-asset-reliance-button"}
                        onClick={() => {
                            history.push(`/Profile/${id}/ClientProfile/EditAssetReliance`);
                            page("Navigate_EditAssetReliance_EditButton");
                            track("Navigate_EditAssetReliance_EditButton");
                        }}>
                    Edit
                </Button>
            </div>
        );
    };

    return (
        <>
            <PresentationPaneHeader
                className="asset-reliance-header-container"
                displayName={displayName}
                title={title}
                titleRow={titleRowChildren()}
            />
            <AlertBanner type={"info"} showAlert={headerOptions.includeLifeInsuranceAtDeath!}>This includes the value of
                the death benefit of your
                Life Insurance policy, which may be included in your taxable estate.</AlertBanner>
        </>

    )
}

export default AssetRelianceHeader;
